import { AnalyticsSkuViewed } from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@ria-client/segment/Analytics/constants';
import { MonetizableEnum } from '@ria-client/segment/enums';
import type { AnalyticsType } from '@ria-client/segment/types';
import { MarketplaceEntityTypeEnum } from '@ria-client/supergraph/marketplace/enums';
import type { ProductCard } from '@ria-client/supergraph/roundup/types';
import { CtaName } from './cta-name';
import { Driver } from './driver';
import { ImageCtaReviewSection } from './image-cta-review-section';
import { OurTake } from './our-take';

const SECTION_NAME = 'product_cards_section';

export interface RoundupProductCardProps {
  card: ProductCard;
  cardIndex: number;
  productPositionBase: number;
}

export function RoundupProductCard({
  card,
  cardIndex,
  productPositionBase,
}: RoundupProductCardProps) {
  const { marketplaceEntity: entity } = card;
  const cta = entity.ctaOverride || entity.cta;
  const elementPosition = cardIndex + productPositionBase;
  const isInstitution = entity.entityType === MarketplaceEntityTypeEnum.Institutions;
  const offerForCta = entity.offerForCta;
  const monetizable = offerForCta?.monetizable ?? entity.monetizable ?? MonetizableEnum.no;
  const offerId = offerForCta?.id || entity.id;
  const offerName = offerForCta?.name || entity.name;
  const ctaAnalytics: AnalyticsType = {
    index: elementPosition,
    ...(entity.institution?.id && { institutionId: entity.institution.id }),
    ...(isInstitution && { institutionId: entity.id }),
    offerId,
    ...(entity.product?.id && { productId: entity.product.id }),
    productType: entity.productType,
    monetizable,
  };

  return (
    <li
      className={`list-none ${cardIndex === 0 ? '' : 'mt-5 md:mt-8'}`}
      data-testid="roundup-product-card"
    >
      <AnalyticsSkuViewed
        as="div"
        className="border-2 border-border"
        additionalAttributes={{ page_type: 'roundup' }}
        impressionPosition={elementPosition}
        impressionSection={SECTION_NAME}
        institutionId={isInstitution ? entity.id : entity.institution?.id}
        institutionName={isInstitution ? entity.name : entity.institution?.name}
        level="offer"
        monetizable={monetizable}
        name={offerName}
        offerId={offerId}
        offerName={offerName}
        productId={entity.product?.id}
        productName={entity.product?.name}
        productType={entity.productType}
        source={SEGMENT_SKU_VIEWED_SOURCE}
      >
        <div className="mt-3 px-3 pb-0 md:px-4 md:pb-3">
          <div className="mb-3">
            {cta ? (
              <CtaName cta={cta} ctaAnalytics={ctaAnalytics} displayName={entity.displayName} />
            ) : (
              <p className="typography-text-large-bold">{entity.displayName}</p>
            )}
          </div>
          <div className="flex flex-col md:flex-row">
            <ImageCtaReviewSection
              cta={cta}
              ctaAnalytics={ctaAnalytics}
              displayName={entity.displayName}
              image={entity.image}
              isFidelityOffer={entity.isFidelityOffer}
              isRiaOffer={entity.isRiaOffer}
              monetizable={monetizable}
              periodConsideredDate={entity.periodConsideredDate}
              riaDisclaimerText={entity.riaDisclaimerText}
              reviewLink={entity.reviewLink}
              reviewedAtDate={entity.reviewedAtDate}
              starRating={entity.starRating}
              starRatingTooltip={entity.starRatingTooltip}
            />
            <div className="mt-3 flex flex-1 flex-col md:mt-0">
              {entity.drivers.map((driver, index) => (
                <Driver
                  borderBottom={index !== entity.drivers.length - 1}
                  driver={driver}
                  key={driver.id}
                />
              ))}
            </div>
          </div>
        </div>
        <OurTake
          bottomLine={card.bottomLine}
          cons={card.cons || entity.structuredContentCons}
          productType={entity.productType}
          pros={card.pros || entity.structuredContentPros}
          structuredContentTagline={entity.structuredContentTagline}
        />
      </AnalyticsSkuViewed>
    </li>
  );
}
